import { InsightsService } from '@percent/domain/insights'
import { baseUrl } from '@percent/partner-dashboard/services/apiPaths'
import { InsightsServiceCapabilities } from './insightsService.types'

type ExportDataset = NonNullable<InsightsService['exportDataset']>

// The app-side HTTP boundary for the generic insights platform, injected into
// `<Insights.Domain service={...}>`. Implements the lib's `InsightsService`
// contract, so the `@percent/domain/insights` lib never owns an Axios client.
//   GET v1/insights/<domain>/metrics/<metric>?<filters>
//   GET v1/insights/<domain>/filters
//   GET v1/insights/<domain>/filters/<filter>/values?search&cursor&pageSize
//   GET v1/insights/<domain>/filters/<filter>/values?value=a&value=b  (label lookup)
//   GET v1/insights/<domain>/datasets/<dataset>?<filters>&sort&direction&cursor&pageSize&columns
//   GET v1/insights/<domain>/datasets/<dataset>/csv?<filters>&columns  (text/csv attachment)
//   POST v1/insights/<domain>/datasets/<dataset>/csv {<filters>, columns, columnGroups}  (same, args in the body)
export const insightsService = ({
  percentClient
}: InsightsServiceCapabilities): InsightsService & { exportDatasetByPost: ExportDataset } => ({
  getMetric: ({ domain, metric, params }) =>
    percentClient.get(`${baseUrl}insights/${domain}/metrics/${metric}`, { params }),
  getFilters: ({ domain }) => percentClient.get(`${baseUrl}insights/${domain}/filters`),
  getFilterValues: ({ domain, filter, params }) =>
    percentClient.get(`${baseUrl}insights/${domain}/filters/${filter}/values`, { params }),
  getDataset: ({ domain, dataset, params }) =>
    percentClient.get(`${baseUrl}insights/${domain}/datasets/${dataset}`, { params }),
  // Full-result CSV export (NEX-712). The endpoint streams a `text/csv` body, so
  // request it as text and let the lib trigger the download.
  exportDataset: ({ domain, dataset, params }) =>
    percentClient.get(`${baseUrl}insights/${domain}/datasets/${dataset}/csv`, {
      params,
      responseType: 'text'
    }),
  // The same export for a selection too long for a URL; the body carries the query's own strings.
  exportDatasetByPost: ({ domain, dataset, params }) =>
    percentClient.post(
      `${baseUrl}insights/${domain}/datasets/${dataset}/csv`,
      Object.fromEntries(
        Object.entries(params).map(([key, value]) => [key, typeof value === 'number' ? String(value) : value])
      ),
      { responseType: 'text' }
    )
})
